import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Heart, ArrowRight, XCircle } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;

    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (!active) return;
        if (res.payment_status === "paid") {
          setDetails(res);
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(res.payment_status)) {
          setState("error");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!active) return;
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <section className="min-h-[70vh] grid place-items-center px-4 py-24 relative z-10" data-testid="payment-success-page">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl border border-plum/10 p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="text-gold-dark mx-auto animate-spin" size={48} data-testid="payment-checking" />
            <h1 className="font-heading text-3xl text-plum font-semibold mt-6">Confirming your gift...</h1>
            <p className="text-stone mt-3">Please hold on a moment while we confirm your donation.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="text-green-600 mx-auto" size={56} data-testid="payment-success-icon" />
            <h1 className="font-heading text-4xl text-plum font-semibold mt-6">Thank you!</h1>
            <p className="text-stone mt-4 text-lg">
              Your {details?.frequency === "monthly" ? "monthly " : ""}gift
              {details?.amount ? ` of $${Number(details.amount).toFixed(2)}` : ""} has been received.
              {details?.designation ? ` It will support ${details.designation}.` : ""}
            </p>
            <p className="text-stone mt-3">A receipt has been emailed to you. Your generosity is changing lives.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <Link to="/" className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1" data-testid="success-home-btn">
                <Heart size={17} /> Back Home
              </Link>
              <Link to="/programs" className="border border-plum/25 text-plum rounded-full px-7 py-3.5 hover:bg-plum hover:text-cream transition-colors flex items-center gap-2" data-testid="success-programs-btn">
                See Our Programs <ArrowRight size={16} />
              </Link>
            </div>
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="text-gold-dark mx-auto" size={48} />
            <h1 className="font-heading text-3xl text-plum font-semibold mt-6">Almost there</h1>
            <p className="text-stone mt-3">Your payment is still processing. If it was completed, you'll receive a receipt by email shortly. Thank you for your patience.</p>
            <Link to="/" className="inline-flex mt-8 bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 items-center gap-2 transition-all hover:-translate-y-1" data-testid="pending-home-btn">
              <Heart size={17} /> Back Home
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="text-red-500 mx-auto" size={52} data-testid="payment-error-icon" />
            <h1 className="font-heading text-3xl text-plum font-semibold mt-6">We couldn't confirm your gift</h1>
            <p className="text-stone mt-3">If you were charged, don't worry — please reach out and we'll sort it out. Otherwise, you can try again.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <Link to="/get-involved" className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1" data-testid="error-retry-btn">
                Try Again
              </Link>
              <Link to="/contact" className="border border-plum/25 text-plum rounded-full px-7 py-3.5 hover:bg-plum hover:text-cream transition-colors" data-testid="error-contact-btn">
                Contact Us
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
